"use client"

import { useState, useEffect } from "react"
import { X } from "lucide-react"

interface SettingsModalProps {
  isOpen: boolean
  onClose: () => void
  systemPrompt: string
  setSystemPrompt: (prompt: string) => void
  temperature: number
  setTemperature: (temp: number) => void
}

export default function SettingsModal({
  isOpen,
  onClose,
  systemPrompt,
  setSystemPrompt,
  temperature,
  setTemperature,
}: SettingsModalProps) {
  const [localPrompt, setLocalPrompt] = useState(systemPrompt)
  const [localTemperature, setLocalTemperature] = useState(temperature)

  useEffect(() => {
    setLocalPrompt(systemPrompt)
    setLocalTemperature(temperature)
  }, [isOpen, systemPrompt, temperature])

  const handleSave = () => {
    setSystemPrompt(localPrompt)
    setTemperature(localTemperature)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-lg">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-medium text-[#4a4a4a]">Settings</h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[#f5f5f5]">
            <X size={20} className="text-[#6b6b6b]" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="systemPrompt" className="block text-sm font-medium text-[#6b6b6b] mb-1">
              System Prompt
            </label>
            <textarea
              id="systemPrompt"
              value={localPrompt}
              onChange={(e) => setLocalPrompt(e.target.value)}
              className="w-full p-3 rounded-xl border border-[#e6e4e3] focus:outline-hidden focus:ring-2 focus:ring-[#d8e8dc] min-h-[100px]"
              placeholder="Instructions for the AI model..."
            />
            <p className="text-xs text-[#9a9a9a] mt-1">This prompt will guide the AI's behavior and responses.</p>
          </div>

          <div>
            <label htmlFor="temperature" className="block text-sm font-medium text-[#6b6b6b] mb-1">
              Temperature: {localTemperature.toFixed(1)}
            </label>
            <input
              id="temperature"
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={localTemperature}
              onChange={(e) => setLocalTemperature(Number.parseFloat(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-[#9a9a9a]">
              <span>More precise</span>
              <span>More creative</span>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="py-2 px-4 rounded-xl border border-[#e6e4e3] text-[#6b6b6b] hover:bg-[#f5f5f5]"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="py-2 px-4 rounded-xl bg-[#d8e8dc] text-[#4a4a4a] hover:bg-[#c5d8c9]"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
